import { useCallback, useEffect, useRef, useState } from 'react';
import type { LiveChange, LiveGame, LiveView } from '@edh/shared';
import { AMEND_WINDOW_MS, deriveView, mergeChanges } from '@edh/shared';
import { OfflineError, request } from './api';
import { db } from './db';

/**
 * A shared game, from this phone's point of view.
 *
 * Three things are happening at once and it is worth being clear which is
 * which:
 *
 *   - the **server's** copy, refetched every couple of seconds;
 *   - the **outbox**, changes this phone has made and is not yet sure landed;
 *   - the **burst**, taps that have not finished being taps yet.
 *
 * What you see is all three added together, so the number under your thumb
 * moves the instant you touch it whatever the network is doing. As the server
 * confirms each change it drops out of the outbox and the answer does not
 * change, which is the whole point of appends that commute.
 */

const POLL_MS = 2000;
const OUTBOX_KEY = 'liveOutbox';

/** How long a burst of taps stays open before it is written down as one change. */
export const SETTLE_MS = 1400;

export type LogKind = 'life' | 'poison' | 'commander';

// ---- Requests ---------------------------------------------------------------

export const listGames = () =>
  request<{ games: LiveGame[] }>('GET', '/games').then((r) => r.games);

/** A pool makes it a draft; leaving it off makes it a standard table. */
export const createGame = (size: number, pool?: string[]) =>
  request<LiveGame>('POST', '/games', pool ? { size, pool } : { size });

/** What the host last said was physically on the table, if it was today. */
export const draftPool = () =>
  request<{ deckIds: string[]; setAt: number }>('GET', '/draft-pool');

export const draftAct = (id: string, kind: 'ban' | 'pick', deckId: string) =>
  request<LiveGame>('POST', `/games/${id}/draft`, { kind, deckId });

export const fetchGame = (id: string) => request<LiveGame>('GET', `/games/${id}`);

export const claimSeat = (
  id: string,
  input: { seat: number; pilotId?: string | null; deckId?: string | null; leave?: boolean },
) => request<LiveGame>('POST', `/games/${id}/seat`, input);

export const startGame = (id: string) => request<LiveGame>('POST', `/games/${id}/start`);

export const passTurn = (id: string) => request<LiveGame>('POST', `/games/${id}/turn`);

/**
 * Take the last pass back, for a turn that ended a beat too early.
 *
 * Whoever holds the turn, whoever is getting it back, or the host. No
 * confirmation: this is already an undo, and undoing it is one tap of End Turn.
 */
export const takeTurnBack = (id: string) =>
  request<LiveGame>('POST', `/games/${id}/unturn`);

/**
 * Declare somebody out, or put them back in.
 *
 * Anyone at the table may, because anyone at the table can see it happen — and
 * the person conceding is often the one who has stopped looking at their phone.
 *
 * `by` is who knocked them out, which is worth rating points and so is only
 * ever a person's answer — never the app's guess at who held priority. Null for
 * a concede, a board wipe that caught its own caster, or anyone the table did
 * not want to credit.
 */
export const markOut = (id: string, seat: number, out: boolean, by?: string | null) =>
  request<LiveGame>('POST', `/games/${id}/out`, { seat, out, by: by ?? null });

/** Host only. Moves a seat round the table without touching anything logged. */
export const reorderTable = (id: string, from: number, to: number) =>
  request<LiveGame>('POST', `/games/${id}/order`, { from, to });

export const endGame = (id: string) => request<{ ended: string }>('DELETE', `/games/${id}`);

/** Records which match this game became, so every phone can find the result. */
export const finishGame = (id: string, matchId: string) =>
  request<LiveGame>('POST', `/games/${id}/finish`, { matchId });

export const sendChanges = (id: string, kind: LogKind, changes: LiveChange[]) =>
  request<LiveGame>('POST', `/games/${id}/log`, { kind, changes });

// ---- The outbox -------------------------------------------------------------

interface Outbox {
  gameId: string;
  changes: { kind: LogKind; change: LiveChange }[];
}

/**
 * Persisted, because a phone that dies at turn nine should not take the last
 * four turns of damage with it. Appends commute, so whatever is still here when
 * it comes back merges correctly however long it has been.
 */
async function readOutbox(gameId: string): Promise<Outbox['changes']> {
  const row = await db.meta.get(OUTBOX_KEY);
  const saved = row?.value as Outbox | undefined;
  return saved?.gameId === gameId ? saved.changes : [];
}

async function writeOutbox(gameId: string, changes: Outbox['changes']): Promise<void> {
  if (changes.length === 0) await db.meta.delete(OUTBOX_KEY);
  else await db.meta.put({ key: OUTBOX_KEY, value: { gameId, changes } satisfies Outbox });
}

const LOG_FIELD = { life: 'log', poison: 'poisonLog', commander: 'commanderLog' } as const;

/** The server's game with this phone's unconfirmed changes folded back in. */
function withOutbox(game: LiveGame, outbox: Outbox['changes']): LiveGame {
  if (outbox.length === 0) return game;
  const merged = { ...game };
  for (const kind of ['life', 'poison', 'commander'] as const) {
    const mine = outbox.filter((o) => o.kind === kind).map((o) => o.change);
    if (mine.length === 0) continue;
    const field = LOG_FIELD[kind];
    merged[field] = mergeChanges(game[field] ?? [], mine);
  }
  return merged;
}

const uuid = () =>
  crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;

export interface LiveSession {
  game: LiveGame | null;
  view: LiveView | null;
  /** True until the first answer, so nothing decides anything from an absence. */
  loading: boolean;
  /** The game is gone — the host ended it, or it never existed. */
  gone: boolean;
  online: boolean;
  /** How many changes this phone is still holding. */
  queued: number;

  /** Life totals with the in-flight burst applied, so the number moves on tap. */
  lifeOf: (seat: number) => number;
  /**
   * The stroke in progress on a seat: what it comes to so far, and when it
   * stops being open to correction. Null once it has closed.
   */
  strokeOf: (seat: number) => { delta: number; at: number } | null;
  bumpLife: (seat: number, delta: number) => void;
  bumpPoison: (seat: number, delta: number) => void;
  bumpCommander: (seat: number, from: string, delta: number) => void;
  /** Force a refetch — after an action of your own that changed the document. */
  refresh: () => Promise<void>;
  /** Replace the local copy outright, for a call that already returned one. */
  put: (game: LiveGame) => void;
}

export function useLiveGame(id: string | null): LiveSession {
  const [game, setGame] = useState<LiveGame | null>(null);
  const [loading, setLoading] = useState(true);
  const [gone, setGone] = useState(false);
  const [online, setOnline] = useState(true);
  const [outbox, setOutbox] = useState<Outbox['changes']>([]);
  const [burst, setBurst] = useState<Record<number, number>>({});

  const gameRef = useRef(game);
  gameRef.current = game;
  const outboxRef = useRef(outbox);
  outboxRef.current = outbox;
  const burstRef = useRef(burst);
  burstRef.current = burst;
  const timers = useRef<Record<number, number>>({});
  /**
   * What each seat's current stroke comes to, for the screen rather than the
   * log — a running total across the whole window in which taps still fold
   * together, which is longer than the burst that gets written down.
   */
  const [strokes, setStrokes] = useState<Record<number, { delta: number; at: number }>>({});
  const strokeTimers = useRef<Record<number, number>>({});
  /**
   * Who had priority when a burst began, not when it settled — a burst can
   * outlive the turn it started in, and the damage happened at the start.
   */
  const creditedTo = useRef<Record<number, string | null>>({});
  /**
   * Which turn each seat's open burst began on.
   *
   * Captured at the first tap for the same reason the credit is: a burst can
   * outlive the turn it started in, and eleven damage dealt on your turn does
   * not become eleven damage dealt on mine because the settle timer landed
   * after you passed.
   */
  const startedOn = useRef<Record<number, number>>({});
  /**
   * The last change written for each seat, so a correction can point at it.
   *
   * Held per seat rather than globally: two people taking damage in the same
   * combat are two strokes, and neither is fixing the other.
   */
  const lastWritten = useRef<Record<number, { id: string; at: number; delta: number }>>({});

  // Anything left from a previous session of the same game, before the first poll.
  useEffect(() => {
    if (!id) return;
    void readOutbox(id).then(setOutbox);
  }, [id]);

  /*
   * Let go of the game when the screen does.
   *
   * The hook stopped polling when the id went away but kept its last copy, so
   * anything rendering off `session.game` went on rendering a game nobody was
   * watching any more — and a result screen dismissed by clearing the id came
   * straight back, because the game it was drawn from was still sitting here.
   */
  useEffect(() => {
    if (id) return;
    setGame(null);
    setGone(false);
    setLoading(false);
  }, [id]);

  const flush = useCallback(
    async (gameId: string) => {
      const held = outboxRef.current;
      if (held.length === 0) return;
      for (const kind of ['life', 'poison', 'commander'] as const) {
        const mine = held.filter((o) => o.kind === kind).map((o) => o.change);
        if (mine.length === 0) continue;
        // Idempotent by change id, so a resend after an ambiguous failure is
        // free — the server drops anything it has already seen.
        const updated = await sendChanges(gameId, kind, mine);
        setGame(updated);
      }
      setOutbox((held2) => {
        const sent = new Set(held.map((o) => o.change.id));
        const rest = held2.filter((o) => !sent.has(o.change.id));
        void writeOutbox(gameId, rest);
        return rest;
      });
    },
    [],
  );

  const poll = useCallback(async () => {
    if (!id) return;
    try {
      await flush(id);
      const fresh = await fetchGame(id);
      setGame(fresh);
      setOnline(true);
      setGone(false);
    } catch (err) {
      if (err instanceof OfflineError) {
        setOnline(false);
      } else if ((err as { status?: number }).status === 404) {
        // The host ended it. Not an error — the expected end of every game.
        setGone(true);
      }
    } finally {
      setLoading(false);
    }
  }, [id, flush]);

  /**
   * Two seconds while somebody is looking, nothing at all while they are not.
   *
   * Four phones on a table for forty minutes is the battery budget this feature
   * spends, and a backgrounded phone polling a game nobody is watching spends
   * it for nothing. Waking refetches immediately rather than waiting out the
   * interval, so the game is current before the screen has finished turning on.
   */
  useEffect(() => {
    if (!id) return;
    let timer = 0;
    const tick = () => {
      if (document.visibilityState === 'visible') void poll();
      timer = window.setTimeout(tick, POLL_MS);
    };
    void poll();
    timer = window.setTimeout(tick, POLL_MS);

    const onVisible = () => {
      if (document.visibilityState === 'visible') void poll();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [id, poll]);

  // Any burst still open when the counter unmounts is written down rather than
  // dropped: closing the app mid-tap should not undo the tap.
  useEffect(() => {
    const open = timers.current;
    return () => {
      for (const t of Object.values(open)) window.clearTimeout(t);
    };
  }, []);

  const merged = game ? withOutbox(game, outbox) : null;
  const view = merged ? deriveView(merged) : null;

  const append = useCallback(
    (kind: LogKind, change: LiveChange) => {
      const gameId = gameRef.current?.id;
      if (!gameId) return;
      setOutbox((held) => {
        const next = [...held, { kind, change }];
        void writeOutbox(gameId, next);
        return next;
      });
      // Straight out rather than waiting for the next poll: the other phones
      // are two seconds behind at worst and there is no reason to make it four.
      void sendChanges(gameId, kind, [change])
        .then((updated) => {
          setGame(updated);
          setOutbox((held) => {
            const rest = held.filter((o) => o.change.id !== change.id);
            void writeOutbox(gameId, rest);
            return rest;
          });
        })
        .catch(() => {
          // Stays in the outbox; the next poll tries again.
        });
    },
    [],
  );

  const settle = useCallback(
    (seat: number) => {
      const delta = burstRef.current[seat] ?? 0;
      const by = creditedTo.current[seat] ?? null;
      const step = startedOn.current[seat];
      setBurst((b) => {
        const { [seat]: _done, ...rest } = b;
        return rest;
      });
      delete creditedTo.current[seat];
      delete startedOn.current[seat];
      if (delta === 0) return;

      const current = gameRef.current;
      if (!current) return;
      const at = deriveView(current);

      /*
       * Going back the other way soon after is a correction, not a heal.
       *
       * Nine down and two back up is one stroke of seven, and the two rarely
       * arrive inside the same burst — noticing an overshoot takes longer than
       * a second and a half. Pointing the second at the first says so without
       * touching the first, which may already be on three other phones.
       */
      const previous = lastWritten.current[seat];
      const corrects =
        previous &&
        Date.now() - previous.at < AMEND_WINDOW_MS &&
        Math.sign(delta) !== Math.sign(previous.delta);

      const change: LiveChange = {
        id: uuid(),
        seat,
        delta,
        turn: at.turn,
        // Where the burst began, falling back to where it ended for a change
        // that somehow has no start recorded.
        step: step ?? (current.turnLog ?? []).length,
        at: Date.now(),
        by,
        ...(corrects ? { amends: previous!.id } : {}),
      };

      // A correction keeps pointing at the original, so a second one folds in
      // too, and the window rolls forward from the latest touch.
      lastWritten.current[seat] = corrects
        ? { id: previous!.id, at: Date.now(), delta: previous!.delta + delta }
        : { id: change.id, at: Date.now(), delta };

      append('life', change);
    },
    [append],
  );

  const bumpLife = useCallback(
    (seat: number, delta: number) => {
      const current = gameRef.current;
      if (!current) return;
      if (creditedTo.current[seat] === undefined) {
        const at = deriveView(current);
        const active = at.seats[at.activeSeat]?.pilotId ?? null;
        // Nobody is credited for their own life total moving on their own turn.
        creditedTo.current[seat] =
          active === current.seats[seat]?.pilotId ? null : active;
        startedOn.current[seat] = (current.turnLog ?? []).length;
      }
      setBurst((b) => ({ ...b, [seat]: (b[seat] ?? 0) + delta }));
      window.clearTimeout(timers.current[seat]);
      timers.current[seat] = window.setTimeout(() => settle(seat), SETTLE_MS);

      // And the stroke, which outlives the burst: it stays open for as long as
      // a correction would still fold into it, and the screen counts it down.
      setStrokes((all) => {
        const prev = all[seat];
        const open = prev && Date.now() - prev.at < AMEND_WINDOW_MS;
        return { ...all, [seat]: { delta: (open ? prev.delta : 0) + delta, at: Date.now() } };
      });
      window.clearTimeout(strokeTimers.current[seat]);
      strokeTimers.current[seat] = window.setTimeout(() => {
        setStrokes(({ [seat]: _closed, ...rest }) => rest);
      }, AMEND_WINDOW_MS);
    },
    [settle],
  );

  /**
   * Poison and commander damage are written the moment they are tapped.
   *
   * No settle window, unlike life: they move one or two at a time rather than
   * in bursts of nine, and both are checked against a threshold that decides
   * whether somebody is dead. A second and a half of "are they out or not" is
   * exactly the wrong thing to be vague about.
   */
  const bumpPoison = useCallback(
    (seat: number, delta: number) => {
      const current = gameRef.current;
      if (!current || delta === 0) return;
      const at = deriveView(current);
      const active = at.seats[at.activeSeat]?.pilotId ?? null;
      append('poison', {
        id: uuid(),
        seat,
        delta,
        turn: at.turn,
        step: (current.turnLog ?? []).length,
        at: Date.now(),
        by: active === current.seats[seat]?.pilotId ? null : active,
      });
    },
    [append],
  );

  const bumpCommander = useCallback(
    (seat: number, from: string, delta: number) => {
      const current = gameRef.current;
      if (!current || delta === 0) return;
      const at = deriveView(current);
      append('commander', {
        id: uuid(),
        seat,
        delta,
        source: from,
        turn: at.turn,
        step: (current.turnLog ?? []).length,
        at: Date.now(),
        by: from,
      });
    },
    [append],
  );

  const lifeOf = useCallback(
    (seat: number) => (view?.seats[seat]?.life ?? 0) + (burst[seat] ?? 0),
    [view, burst],
  );

  const strokeOf = useCallback((seat: number) => strokes[seat] ?? null, [strokes]);

  return {
    game: merged,
    view,
    loading,
    gone,
    online,
    queued: outbox.length,
    lifeOf,
    strokeOf,
    bumpLife,
    bumpPoison,
    bumpCommander,
    refresh: poll,
    put: setGame,
  };
}
